import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { SystemErrorLog } from '../types';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Trash2, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { safeFormatDate } from '../lib/utils';

export default function ErrorLogsViewer() {
  const [logs, setLogs] = useState<SystemErrorLog[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'system_errors'), orderBy('timestamp', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setLogs(snap.docs.map(d => ({ id: d.id, ...d.data() } as SystemErrorLog)));
    }, (error) => {
      // Need fallback if index is missing
      if (error.message && error.message.includes('requires an index')) {
        console.warn('Firestore index needed for system_errors:', error.message);
        const fallbackQ = collection(db, 'system_errors');
        const fallbackUnsub = onSnapshot(fallbackQ, (fallbackSnap) => {
          const fetched = fallbackSnap.docs.map(d => ({ id: d.id, ...d.data() } as SystemErrorLog));
          fetched.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
          setLogs(fetched);
        });
        return () => fallbackUnsub();
      } else {
        handleFirestoreError(error, OperationType.LIST, 'system_errors');
      }
    });
    return () => unsub();
  }, []);

  const handleResolve = async (id: string, currentStatus: boolean) => {
    try {
      await updateDoc(doc(db, 'system_errors', id), { resolved: !currentStatus });
      toast.success(currentStatus ? 'Error marked as unresolved' : 'Error marked as resolved');
    } catch (err) {
      handleFirestoreError(err, OperationType.UPDATE, 'system_errors');
    }
  };

  const handlePurgeResolved = async () => {
    const resolvedLogs = logs.filter(l => l.resolved);
    if (resolvedLogs.length === 0) {
      toast.info('No resolved logs to purge');
      return;
    }
    
    if (confirm(`Are you sure you want to permanently delete ${resolvedLogs.length} resolved error logs?`)) {
      try {
        const batch = writeBatch(db);
        resolvedLogs.forEach(log => {
          batch.delete(doc(db, 'system_errors', log.id!));
        });
        await batch.commit();
        toast.success(`Purged ${resolvedLogs.length} logs`);
      } catch (err) {
        handleFirestoreError(err, OperationType.DELETE, 'system_errors');
      }
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-[var(--background)] border-l-4 border-lcars-red p-4 rounded-r-xl">
        <div>
          <h2 className="text-xl font-bold text-lcars-red uppercase tracking-widest flex items-center gap-2">
            <AlertTriangle className="w-6 h-6" /> System Diagnostics
          </h2>
          <p className="text-sm text-[var(--foreground)]/60">Intercepted framework and database anomalies</p>
        </div>
        <Button 
          variant="outline"
          onClick={handlePurgeResolved}
          className="border-lcars-red text-lcars-red hover:bg-lcars-red hover:text-black uppercase tracking-widest text-xs font-bold"
        >
          <Trash2 className="w-4 h-4 mr-2" /> Purge Resolved
        </Button>
      </div>

      <div className="space-y-4">
        {logs.length === 0 ? (
          <div className="p-8 text-center border border-white/10 rounded-xl bg-[var(--background)]">
            <p className="text-[var(--foreground)]/40 uppercase tracking-widest font-bold">Systems Nominal. No errors logged.</p>
          </div>
        ) : (
          logs.map(log => (
            <div key={log.id} className={`border rounded-xl overflow-hidden ${log.resolved ? 'border-lcars-blue/30 bg-[var(--background)]' : 'border-lcars-red/50 bg-lcars-red/5'}`}>
              <div 
                className={`p-4 cursor-pointer flex justify-between items-center hover:bg-white/5 transition-colors ${log.resolved ? 'opacity-70' : ''}`}
                onClick={() => toggleExpand(log.id!)}
              >
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${log.resolved ? 'bg-lcars-blue/20 text-lcars-blue' : 'bg-lcars-red/20 text-lcars-red'}`}>
                    <AlertTriangle className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className={`font-bold uppercase tracking-widest ${log.resolved ? 'text-lcars-blue' : 'text-lcars-red'}`}>
                      {log.type} Error
                    </h3>
                    <p className="text-sm text-[var(--foreground)]/80 line-clamp-1 max-w-lg">{log.message}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-[var(--foreground)]/40">
                      <span>{safeFormatDate(log.timestamp, 'MMM dd, yyyy HH:mm:ss')}</span>
                      <span>•</span>
                      <span>User: {log.userEmail || log.userId || 'Anonymous'}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleResolve(log.id!, log.resolved); }}
                    className={`p-2 rounded-full transition-colors z-10 ${log.resolved ? 'text-lcars-blue hover:bg-lcars-blue/20' : 'text-[var(--foreground)]/40 hover:text-lcars-gold hover:bg-white/10'}`}
                    title={log.resolved ? "Mark Unresolved" : "Mark Resolved"}
                  >
                    <CheckCircle2 className="w-6 h-6" />
                  </button>
                  {expandedId === log.id ? <ChevronUp className="w-5 h-5 text-[var(--foreground)]/40" /> : <ChevronDown className="w-5 h-5 text-[var(--foreground)]/40" />}
                </div>
              </div>

              {expandedId === log.id && (
                <div className="p-4 bg-[var(--background)] border-t border-white/10 space-y-4">
                  <div>
                    <span className="text-xs text-lcars-blue font-bold uppercase tracking-widest block mb-1">Message</span>
                    <p className="text-sm text-[var(--foreground)]/90 bg-white/5 p-3 rounded font-mono break-words">{log.message}</p>
                  </div>
                  <div>
                    <span className="text-xs text-lcars-blue font-bold uppercase tracking-widest block mb-1">Stack Trace / Details</span>
                    <pre className="text-xs text-[var(--foreground)]/70 bg-white/5 p-3 rounded font-mono overflow-auto max-h-60 whitespace-pre-wrap">{log.details}</pre>
                  </div>
                  {log.path && (
                    <div>
                      <span className="text-xs text-lcars-blue font-bold uppercase tracking-widest block mb-1">Path</span>
                      <code className="text-sm text-lcars-gold bg-lcars-gold/10 px-2 py-1 rounded">{log.path}</code>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
